import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { NgClass, AsyncPipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { NavigationEnd, Router } from '@angular/router';

import { combineLatest, distinctUntilChanged, filter } from 'rxjs';

import { AdobeAnalyticsService } from 'src/app/modules/core/services/adobe-analytics.service';
import { AuthService } from 'src/app/modules/core/services/auth.service';
import { BrandService } from 'src/app/modules/core/services/brand.service';
import { PostCloseDateService } from 'src/app/modules/core/services/post-close-date.service';
import { RealtorActionsService } from 'src/app/modules/core/services/realtor-actions.service';
import { SettingsService } from 'src/app/modules/core/services/settings.service';
import { FeedbackActionsService } from 'src/app/modules/core/services/feedback-actions.service';

import { HomeDetails } from 'src/app/modules/core/models/sales.model';

import { AppFacade } from 'src/app/modules/ngrx-store/app/app.facade';
import { YourHomeFacade } from 'src/app/modules/ngrx-store/your-home/your-home.facade';

import { InteractionContext } from 'src/app/modules/shared/constants/adobe-analytics-fields';
import { NAVIGATION_ROUTES, REALTOR_ROUTES } from 'src/app/modules/shared/constants/route-paths';
import { IfViewSizeDirective } from 'src/app/modules/shared/directives/if-view-size.directive';
import { BrandMapItem } from 'src/app/modules/shared/types/brand';
import { keyPressHandler } from 'src/app/modules/shared/utilities/accessibility.utils';
import { getCtaClickProps, getPageLoadEvars } from 'src/app/modules/shared/utilities/adobe-analytics.utils';
import { getPageNameFromUrl } from 'src/app/modules/shared/utilities/page-name.utils';
import { UnsubscribeOnDestroy } from 'src/app/modules/shared/utilities/unsubscribe-on-destroy';

@Component({
	selector: 'cp-header',
	templateUrl: './header.component.html',
	styleUrls: ['./header.component.scss'],
	imports: [NgClass, IfViewSizeDirective, MatIconButton, MatIcon, CdkOverlayOrigin, CdkConnectedOverlay, MatButton, AsyncPipe]
})

export class HeaderComponent extends UnsubscribeOnDestroy implements OnInit {
	isPreviewRealtor = input<boolean | undefined>();

	@Output() hamburgerClicked = new EventEmitter<MouseEvent>();

	@Input() isMyAccountEnabled: boolean = true;

	@ViewChild(CdkOverlayOrigin) myAccountOrigin!: CdkOverlayOrigin;
	
	@ViewChild(CdkConnectedOverlay) myAccountOverlay!: CdkConnectedOverlay;

	@ViewChildren('myAccountMenuAction', { read: ElementRef }) myAccountMenuActions!: QueryList<ElementRef<HTMLElement>>;

	userInfo$ = this.appFacade.userInfo$;

	realtorInfo$ = this.yourHomeFacade.realtorInfo$;

	pageName: string = '';

	brand?: BrandMapItem;

	logoSrc?: string;

	myAccountUrl: string | undefined;
	
	myAccountToggle = false;

	isPreview: boolean = false;

	isRealtor: boolean = false;
	
	isThirtyOneDaysPostClose: boolean = false;

	isRealtorEnabled: boolean = false; // Global/Market-level flag

	isOnClientManagementPage = false;

	private homeDetails: HomeDetails | null = null;

	myAccountOverlayPositions: ConnectedPosition[] = [
		{
			originX: 'end',
			originY: 'bottom',
			overlayX: 'end',
			overlayY: 'top',
			offsetY: 8,
		},
	];

	keyPressHandler = keyPressHandler;

	constructor(
		private authService: AuthService,
		private brandService: BrandService,
		private appFacade: AppFacade,
		private settingsService: SettingsService,
		private adobeAnalyticsService: AdobeAnalyticsService,
		private router: Router,
		private yourHomeFacade: YourHomeFacade,
		private realtorActionsService: RealtorActionsService,
		private postCloseDateService: PostCloseDateService,
		private feedbackActionsService: FeedbackActionsService
	) {
		super();

		this.settingsService.getSettings().pipe(
			this.takeUntilDestroyed()
		).subscribe(settings => {
			const brand = this.brandService.getBrand();

			this.myAccountUrl = settings.brandConfigs[brand.name].myAccountUrl;
		});
	}

	ngOnInit(): void {
		this.brand = this.brandService.getBrand();
		this.logoSrc = this.brandService.getBrandImage();
		this.isPreview = this.authService.isPreviewUser();
		this.isRealtor = this.authService.isRealtorUser();

		// Keep header in sync when the brand changes (e.g. realtor switching between client cards)
		this.brandService.currentBrand$.pipe(
			this.takeUntilDestroyed()
		).subscribe(brand => {
			this.brand = brand;
			this.logoSrc = this.brandService.getBrandImage();
		});

		// Track client management page state for menu styling and feedback button visibility
		const cmPath = `/${NAVIGATION_ROUTES.Realtor}/${REALTOR_ROUTES.Homes}`;
		this.isOnClientManagementPage = this.isRealtor && this.router.url.startsWith(cmPath);

		// Set pageName on initial load
		this.pageName = getPageNameFromUrl(this.router.url);

		// Listen to route changes and set pageName
		this.router.events.pipe(
			this.takeUntilDestroyed(),
			filter(event => event instanceof NavigationEnd)
		).subscribe((event: NavigationEnd) => {
			this.pageName = getPageNameFromUrl(event.urlAfterRedirects);
			this.isOnClientManagementPage = this.isRealtor && event.urlAfterRedirects.startsWith(cmPath);
		});
		
		this.yourHomeFacade.realtorEnabled$.pipe(
			distinctUntilChanged(),
			this.takeUntilDestroyed()
		).subscribe(realtorEnabled => {
			this.isRealtorEnabled = realtorEnabled;
		});

		combineLatest([
			this.yourHomeFacade.homeDetails$,
			this.settingsService.getSettings() // Settings required for post-close date calculations
		]).pipe(
			this.takeUntilDestroyed()
		).subscribe(([homeDetails]) => {
			this.homeDetails = homeDetails ?? null;
			const closeOfEscrow = homeDetails?.lot.closeOfEscrow;
			this.isThirtyOneDaysPostClose = closeOfEscrow ? this.postCloseDateService.getIsThirtyOneDaysSince(closeOfEscrow) : false;
		});
	}

	//This method was created to toggle our custom cdk overlay; due to having to switch from using the mat-menu due to accessibility issues.
	openOverlay() {
		this.myAccountToggle = !this.myAccountToggle;

		if (this.myAccountToggle) {
			setTimeout(() => this.focusFirstMyAccountMenuAction());
		}

		// Track adobe CTA click
		const evar = getPageLoadEvars(this.pageName);
		const prop = getCtaClickProps(InteractionContext.Viewing, 'Header', 'Global Menu');
		this.adobeAnalyticsService.trackCtaClick(evar, prop);
	}

	closeOverlay() {
		this.myAccountToggle = false;
	}

	onResetPassword(): void {
		this.realtorActionsService.initiatePasswordReset();
	}

	onProfileManagement(): void {
		this.realtorActionsService.initiateProfileManagement();
	}

	onLogout(): void {
		if (!this.isPreview) {
			this.authService.logout();
		}
	}

	onMyAccountClick(): void {
		// Track adobe CTA click for My Account link
		const evar = getPageLoadEvars(this.pageName);
		const prop = getCtaClickProps(InteractionContext.Navigating, 'Header', 'My Account');
		this.adobeAnalyticsService.trackCtaClick(evar, prop);
	}

	onRealtorAccessClick(): void {
		// Close the overlay before navigating
		this.closeOverlay();
		this.realtorActionsService.navigateToRealtorAccess();
	}

	onHamburgerClick(event: MouseEvent): void {
		this.hamburgerClicked.emit(event);
		
		// Track adobe CTA click for Hamburger Menu
		const evar = getPageLoadEvars(this.pageName);
		const prop = getCtaClickProps(InteractionContext.Viewing, 'Header', 'Hamburger Menu');
		this.adobeAnalyticsService.trackCtaClick(evar, prop);
	}

	get showFeedbackButton(): boolean {
		return !!this.homeDetails && !this.isOnClientManagementPage;
	}

	onGiveFeedbackClick(): void {
		this.closeOverlay();
		const evar = getPageLoadEvars(this.pageName);
		const prop = getCtaClickProps(InteractionContext.Navigating, 'Header', 'Give Feedback');
		this.adobeAnalyticsService.trackCtaClick(evar, prop);
		this.feedbackActionsService.openFeedbackModal(this.homeDetails);
	}

	private focusFirstMyAccountMenuAction(): void {
		const firstEnabledAction = this.myAccountMenuActions.toArray().find(menuAction => {
			const actionElement = menuAction.nativeElement;

			return !actionElement.hasAttribute('disabled')
				&& actionElement.getAttribute('aria-disabled') !== 'true';
		});

		firstEnabledAction?.nativeElement.focus();
	}
}